import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api.service';
import { useRealtime } from '../contexts/RealtimeContext';
import { Category, Team } from '../types';

export interface TeamParticipant {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export interface TeamHeat {
  heat_id: string;
  heat_number: number;
  wod_id: string | null;
  wod_number: number | null;
  wod_name: string | null;
  lane_number: number | null;
}

/** Une équipe prête à afficher : participants déjà lus, noms déjà joints. */
export interface TeamRow extends Team {
  participantList: TeamParticipant[];
  participantNames: string;
  heats: TeamHeat[];
}

export const teamsQueryKey = (eventId: string | null) => ['teams', eventId] as const;
export const categoriesQueryKey = (eventId: string | null) => ['categories', eventId] as const;

function parseParticipants(raw: Team['participants'] | any[]): TeamParticipant[] {
  if (!raw) return [];
  try {
    const list = Array.isArray(raw) ? raw : JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.map((p: any) => ({
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      email: p.email || '',
      phone: p.phone || '',
    }));
  } catch {
    return [];
  }
}

export async function fetchTeams(eventId: string): Promise<TeamRow[]> {
  const res = await apiService.getTeams(eventId);
  const teams: any[] = res.data.teams || [];
  return teams.map((t) => {
    const participantList = parseParticipants(t.participants);
    return {
      ...t,
      participantList,
      participantNames: participantList
        .map((p) => `${p.first_name} ${p.last_name}`.trim())
        .filter(Boolean)
        .join(', '),
      heats: Array.isArray(t.heats) ? t.heats : [],
    } as TeamRow;
  });
}

/** Équipes de l'événement, rechargées quand le terrain en modifie une. */
export function useTeams(eventId: string | null) {
  const qc = useQueryClient();
  useRealtime(['team', 'heat', 'heat_participant'], () => {
    if (eventId) qc.invalidateQueries({ queryKey: teamsQueryKey(eventId) });
  });
  return useQuery({
    queryKey: teamsQueryKey(eventId),
    queryFn: () => fetchTeams(eventId as string),
    enabled: !!eventId,
    staleTime: 15 * 1000,
  });
}

export function useCategories(eventId: string | null) {
  const qc = useQueryClient();
  useRealtime(['category'], () => {
    if (eventId) qc.invalidateQueries({ queryKey: categoriesQueryKey(eventId) });
  });
  return useQuery({
    queryKey: categoriesQueryKey(eventId),
    queryFn: async () => ((await apiService.getCategories(eventId as string)).data.categories || []) as Category[],
    enabled: !!eventId,
    staleTime: 60 * 1000,
  });
}
